import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Home, BookOpen, Search, Repeat, Upload as Up } from 'lucide-react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

export function AppShell() {
  const { pathname } = useLocation();
  const { token } = useAuth();
  const { t } = useI18n();
  const nav = [
    { to: '/app',            icon: Home,    label: t('nav.home') },
    { to: '/app/album',      icon: BookOpen,label: t('nav.album') },
    { to: '/app/upload',     icon: Up,      label: t('nav.upload') },
    { to: '/app/matches',    icon: Search,  label: t('nav.match'), badgeKey: 'matches' as const },
    { to: '/app/duplicates', icon: Repeat,  label: t('nav.dupes') },
  ];
  const { data: matchesData } = useQuery({
    queryKey: ['matches', false],
    queryFn: () => api.matches(token!, { limit: 20 }),
    enabled: !!token,
    staleTime: 60_000,
  });
  const matchCount = matchesData?.matches?.length ?? 0;
  return (
    <div className="relative min-h-dvh">
      {/* halos de fondo */}
      <div className="pointer-events-none fixed inset-0 bg-mesh opacity-60" />

      <main className="relative mx-auto max-w-md md:max-w-2xl lg:max-w-3xl px-4 md:px-6 pt-6 md:pt-10 pb-32 text-[15px] md:text-base">
        <Outlet />
      </main>

      <nav className="bottom-nav">
        <div className="relative flex items-end justify-between">
          {nav.map((it) => {
            const active = pathname === it.to;
            const Icon = it.icon;
            const badge = (it as any).badgeKey === 'matches' ? matchCount : 0;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                className="relative flex flex-1 flex-col items-center gap-1 py-2"
              >
                {active && (
                  <motion.div
                    layoutId="navDot"
                    className="absolute -top-1 h-1.5 w-8 rounded-full bg-flame shadow-glow-flame"
                  />
                )}
                <div className="relative">
                  <Icon
                    size={22}
                    className={active ? 'text-flame' : 'text-haze'}
                    strokeWidth={active ? 2.4 : 2}
                  />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-lime text-[10px] font-extrabold text-ink grid place-items-center tabular-nums shadow-[0_0_10px_rgba(198,255,94,0.5)]">
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                <span
                  className={`text-2xs font-semibold ${active ? 'text-foam' : 'text-haze'}`}
                >
                  {it.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
